// Account Deletion View
import { t } from '../i18n.js';
import { getCurrentUser, signOut } from '../firebase/auth.js';
import { deleteUserAccount } from '../firebase/accountDeletion.js';
import router from '../router.js';
import { toast } from '../components/Toast.js';

/**
 * Create delete account view
 * @returns {string} HTML string
 */
export function createDeleteAccountView() {
  const user = getCurrentUser();
  if (!user) return '<div class="page"><div class="container"><p>Access Denied</p></div></div>';

  return `
    <div class="page">
      <div class="container" style="max-width: 600px; padding-top: 4rem;">
        <div class="card" style="border-top: 4px solid var(--color-error);">
          <div style="text-align: center; margin-bottom: 2rem;">
            <div style="font-size: 4rem; margin-bottom: 1rem;">⚠️</div>
            <h1 style="color: var(--color-error);">${t('deleteAccount.title')}</h1>
            <p style="font-weight: 700; color: var(--color-error); margin-bottom: 0.5rem;">${t('deleteAccount.warning')}</p>
          </div>
          
          <div style="background: rgba(255, 68, 68, 0.05); padding: 1.5rem; border-radius: 8px; margin-bottom: 2rem; border-left: 4px solid var(--color-error);">
            <p style="margin-bottom: 1rem;">${t('deleteAccount.explanation')}</p>
            <ul style="margin-left: 1.5rem; color: var(--color-text-secondary); font-size: 0.95rem;">
              <li>${t('nav.dashboard')} & ${t('site.tabDetails')}</li>
              <li>${t('site.tabAssets')} & 3D Models</li>
              <li>${t('credits.creditLabel')} Balance</li>
              <li>${t('site.tabLogbook')} Entry History</li>
            </ul>
          </div>

          <div class="form-group" id="delete-form-container">
            <label class="form-label">${t('deleteAccount.confirmLabel')}</label>
            <input 
              type="text" 
              id="delete-confirm-input" 
              class="form-input" 
              placeholder="${t('deleteAccount.confirmLabel').split('"')[1]}"
              style="text-transform: uppercase;"
            >
            
            <div style="margin-top: 2rem;">
              <button id="btn-confirm-delete" class="btn btn-error btn-lg" style="width: 100%;" disabled>
                ${t('deleteAccount.button')}
              </button>
              <button id="btn-cancel-delete" class="btn btn-ghost btn-lg" style="width: 100%; margin-top: 1rem;">
                ${t('site.cancel')}
              </button>
            </div>
          </div>

          <div id="delete-loading" class="hidden" style="text-align: center; padding: 2rem;">
            <div class="loader-spinner" style="margin: 0 auto 1.5rem;"></div>
            <p>${t('deleteAccount.deleting')}</p>
          </div>
        </div>
      </div>
    </div>
    
    <style>
      .btn-error {
        background: var(--color-error);
        color: white;
      }
      .btn-error:hover:not(:disabled) {
        background: #cc0000;
        transform: translateY(-2px);
      }
      .btn-error:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    </style>
  `;
}

/**
 * Initialize delete account view events
 */
export function initDeleteAccountView() {
  const confirmInput = document.getElementById('delete-confirm-input');
  const deleteBtn = document.getElementById('btn-confirm-delete');
  const cancelBtn = document.getElementById('btn-cancel-delete');
  const formContainer = document.getElementById('delete-form-container');
  const loadingContainer = document.getElementById('delete-loading');

  if (!confirmInput) return;

  const confirmString = t('deleteAccount.confirmLabel').split('"')[1];

  confirmInput.addEventListener('input', (e) => {
    deleteBtn.disabled = e.target.value.trim().toUpperCase() !== confirmString;
  });

  cancelBtn.addEventListener('click', () => {
    router.navigate('/dashboard');
  });

  deleteBtn.addEventListener('click', async () => {
    const user = getCurrentUser();
    if (!user) return;

    formContainer.classList.add('hidden');
    loadingContainer.classList.remove('hidden');

    try {
      await deleteUserAccount(user);
      toast.success(t('deleteAccount.success'));
      // Auth change listener will handle redirect to home
    } catch (error) {
      console.error('Account deletion failed:', error);
      
      formContainer.classList.remove('hidden');
      loadingContainer.classList.add('hidden');

      if (error.code === 'auth/requires-recent-login') {
        toast.error(t('deleteAccount.reauthRequired'));
        // Wait a bit then logout so they can sign back in
        setTimeout(async () => {
          await signOut();
          router.navigate('/login');
        }, 3000);
      } else {
        toast.error(t('deleteAccount.error'));
      }
    }
  });
}
